import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';

// Reusable "notify all or choose suppliers" picker. Consumers pass the eligible
// supplier list (already filtered to those with an active portal user) and get
// back the selection via onConfirm:
//   - all  → onConfirm(null)   (notify everyone eligible)
//   - some → onConfirm([ids])  (notify only the ticked suppliers)
//
// Pure UI: the actual send + logging stays with the caller (risk-flag Notify
// uses the compliance template; the document flows use documentPublishedEmail).
export default function NotifySuppliersModal({
  open,
  title = 'Notify suppliers',
  intro,
  suppliers = [],
  busy = false,
  confirmLabel = 'Notify',
  onConfirm,
  onCancel,
}) {
  const [scope, setScope] = useState('all');       // 'all' | 'choose'
  const [selected, setSelected] = useState(() => new Set());

  // Reset each time the modal opens (default to everyone selected in choose mode).
  useEffect(() => {
    if (!open) return;
    setScope('all');
    setSelected(new Set(suppliers.map(s => s.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const count = scope === 'all' ? suppliers.length : selected.size;
  const allChecked = suppliers.length > 0 && selected.size === suppliers.length;

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setAll = (on) => setSelected(on ? new Set(suppliers.map(s => s.id)) : new Set());

  const confirm = () => {
    if (busy) return;
    if (scope === 'all') onConfirm?.(null);
    else onConfirm?.(Array.from(selected));
  };

  const canSend = suppliers.length > 0 && count > 0;
  const radioCls = "flex items-center gap-2.5 cursor-pointer font-['Metropolis'] text-sm text-fusion-mid-grey";

  const listBody = useMemo(() => (
    <div className="mt-3 border border-line rounded-lg divide-y divide-line max-h-[280px] overflow-y-auto">
      <div className="flex items-center justify-between px-3 py-2 bg-white/[0.03]">
        <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-[#51534A]">
          {selected.size} of {suppliers.length} selected
        </span>
        <button
          type="button"
          onClick={() => setAll(!allChecked)}
          className="font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase text-fusion-forest hover:underline">
          {allChecked ? 'Clear all' : 'Select all'}
        </button>
      </div>
      {suppliers.map(s => (
        <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-white/[0.02]">
          <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} className="accent-[#92D400]" />
          <span className="font-['Metropolis'] text-sm text-ink flex-1 truncate">{s.supplier_name}</span>
          <span className="font-['Metropolis'] text-[10px] text-[#51534A]">{s.userCount} user{s.userCount === 1 ? '' : 's'}</span>
        </label>
      ))}
    </div>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [suppliers, selected, allChecked]);

  if (!open) return null;

  return (
    <Modal open onClose={onCancel} maxWidth={520} ariaLabel={title}>
      <Modal.Header eyebrow="Notify" title={title} onClose={onCancel} />
      <Modal.Body>
        {intro && <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey leading-relaxed mb-4">{intro}</p>}

        {suppliers.length === 0 ? (
          <p className="font-['Metropolis'] text-sm text-[#7A4E10]">
            No eligible suppliers have an active portal user to email.
          </p>
        ) : (
          <div className="flex flex-col gap-2.5">
            <label className={radioCls}>
              <input type="radio" name="notify-scope" checked={scope === 'all'} onChange={() => setScope('all')} className="accent-[#92D400]" />
              Notify all eligible suppliers ({suppliers.length})
            </label>
            <label className={radioCls}>
              <input type="radio" name="notify-scope" checked={scope === 'choose'} onChange={() => setScope('choose')} className="accent-[#92D400]" />
              Choose suppliers
            </label>
            {scope === 'choose' && listBody}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onCancel} disabled={busy}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50">Cancel</button>
          <button type="button" onClick={confirm} disabled={busy || !canSend}
            className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-bold px-5 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
            {busy ? 'Sending…' : `${confirmLabel} ${count} supplier${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
